<script setup lang="ts">
import { toTypedSchema } from "@vee-validate/valibot";
import { useForm } from "vee-validate";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  PinInputInput,
  PinInputRoot,
  VisuallyHidden,
} from "reka-ui";
import { useAuthStore } from "../lib/auth.store";
import {
  loginSchema,
  registrationSchema,
  resetEmailSchema,
  resetOtpSchema,
  resetPasswordSchema,
} from "../lib/form-schemas";

type AuthMode =
  | "login"
  | "register"
  | "reset-email"
  | "reset-otp"
  | "reset-password"
  | "reset-success";

type SmartCaptchaHandle = {
  execute: () => Promise<string>;
};

const authStore = useAuthStore();
const mode = ref<AuthMode>("login");
const resetEmail = ref("");
const resetCode = ref("");
const resetOtpDigits = ref<number[]>([]);
const isResetOtpTouched = ref(false);
const smartCaptcha = ref<SmartCaptchaHandle | null>(null);

const loginForm = useForm({
  validationSchema: toTypedSchema(loginSchema),
  initialValues: { email: "", password: "", consent: false },
});
const registrationForm = useForm({
  validationSchema: toTypedSchema(registrationSchema),
  initialValues: { firstName: "", email: "", password: "", consent: false },
});
const resetEmailForm = useForm({
  validationSchema: toTypedSchema(resetEmailSchema),
  initialValues: { email: "" },
});
const resetOtpForm = useForm({
  validationSchema: toTypedSchema(resetOtpSchema),
  initialValues: { code: "" },
});
const resetPasswordForm = useForm({
  validationSchema: toTypedSchema(resetPasswordSchema),
  initialValues: { password: "", passwordConfirmation: "" },
});

const isPasswordResetMode = computed(() => mode.value.startsWith("reset-"));
const shouldShowResetOtpError = computed(
  () =>
    Boolean(resetOtpForm.errors.value.code) &&
    (isResetOtpTouched.value || resetOtpForm.submitCount.value > 0),
);
const title = computed(() => {
  if (isPasswordResetMode.value) return "Восстановление пароля";
  return mode.value === "login" ? "Войти" : "Регистрация";
});
const description = computed(() => {
  if (mode.value === "reset-email") {
    return "Введите e-mail, который использовали при регистрации.";
  }

  if (mode.value === "reset-otp") {
    return "Мы отправили код подтверждения на почту. Введите его, чтобы продолжить.";
  }

  if (mode.value === "reset-password") {
    return "Придумайте новый пароль и повторите его.";
  }

  if (mode.value === "reset-success") {
    return "Ваш пароль успешно восстановлен!";
  }

  return mode.value === "login"
    ? "Войдите, чтобы видеть историю заказов и быстрее оформлять покупки"
    : "Создайте аккаунт, чтобы сохранять данные и управлять заказами.";
});

async function getCaptchaToken() {
  try {
    return (await smartCaptcha.value?.execute()) ?? "";
  } catch (error) {
    authStore.errorMessage =
      error instanceof Error
        ? error.message
        : "Не удалось выполнить проверку SmartCaptcha.";
    throw error;
  }
}

const submitLogin = loginForm.handleSubmit(async ({ email, password }) => {
  const captchaToken = await getCaptchaToken();
  await authStore.login({ email, password }, captchaToken);
});
const submitRegistration = registrationForm.handleSubmit(
  async ({ firstName, email, password }) => {
    const captchaToken = await getCaptchaToken();
    await authStore.register({ firstName, email, password }, captchaToken);
  },
);
const submitResetEmail = resetEmailForm.handleSubmit(async ({ email }) => {
  const captchaToken = await getCaptchaToken();
  resetEmail.value = email;
  resetOtpDigits.value = [];
  isResetOtpTouched.value = false;
  resetOtpForm.resetForm();
  await authStore.requestPasswordReset(email, captchaToken);
  setMode("reset-otp");
});
const submitResetOtp = resetOtpForm.handleSubmit(({ code }) => {
  resetCode.value = code;
  setMode("reset-password");
});
const submitResetPassword = resetPasswordForm.handleSubmit(
  async ({ password }) => {
    const captchaToken = await getCaptchaToken();
    await authStore.confirmPasswordReset(
      {
        email: resetEmail.value,
        code: resetCode.value,
        password,
      },
      captchaToken,
    );
    setMode("reset-success");
  },
);

function setMode(nextMode: AuthMode) {
  mode.value = nextMode;
  authStore.errorMessage = null;
}

function startPasswordReset() {
  const email = loginForm.values.email?.trim() ?? "";

  resetEmailForm.setFieldValue("email", email);
  setMode("reset-email");
}

function showLogin() {
  resetCode.value = "";
  resetOtpDigits.value = [];
  isResetOtpTouched.value = false;
  mode.value = "login";
  authStore.errorMessage = null;
  resetOtpForm.resetForm();
  resetPasswordForm.resetForm();
}

function updateResetOtp(value: number[]) {
  resetOtpDigits.value = value;
  isResetOtpTouched.value = true;
  resetOtpForm.setFieldValue("code", value.map(String).join(""));
}

function completeResetOtp(value: number[]) {
  updateResetOtp(value);
  void submitResetOtp();
}

async function resendResetCode() {
  if (!resetEmail.value) return;

  try {
    const captchaToken = await getCaptchaToken();
    resetOtpDigits.value = [];
    isResetOtpTouched.value = false;
    resetOtpForm.resetForm();
    await authStore.requestPasswordReset(resetEmail.value, captchaToken);
  } catch {
    // The store already exposes the user-facing error message.
  }
}

function handleFormEnter(event: KeyboardEvent) {
  if (event.isComposing || !(event.target instanceof HTMLInputElement)) return;
  if (event.target.type === "checkbox") return;
  event.preventDefault();
  if (authStore.isSubmitting) return;
  const form = event.target.form;
  const submitButton = form?.querySelector<HTMLButtonElement>('button[type="submit"]');
  if (submitButton && !submitButton.disabled) form?.requestSubmit(submitButton);
}

function setOpen(isOpen: boolean) {
  if (isOpen) {
    authStore.isOpen = true;
    return;
  }

  authStore.close();
}
</script>

<template>
  <DialogRoot :open="authStore.isOpen" @update:open="setOpen">
    <DialogPortal>
      <DialogOverlay
        class="auth-drawer-overlay dialog-overlay fixed inset-0 z-[85] bg-black/35"
      />
      <DialogContent
        class="auth-drawer-content fixed inset-x-0 bottom-0 z-[90] flex max-h-[calc(100dvh-0.75rem)] flex-col overflow-y-auto rounded-t-2xl bg-white px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] outline-none lg:inset-y-0 lg:right-auto lg:h-screen lg:max-h-none lg:w-[28rem] lg:rounded-none lg:px-8 lg:py-8 lg:pt-22"
      >
        <div
          class="flex h-11 w-full shrink-0 items-center justify-center lg:hidden"
        >
          <span aria-hidden="true" class="h-1 w-10 rounded-full bg-black/15" />
        </div>
        <VisuallyHidden as-child>
          <DialogDescription
            >Вход или регистрация в личном кабинете</DialogDescription
          >
        </VisuallyHidden>
        <DialogClose
          type="button"
          class="flex size-6 absolute top-3 right-3 cursor-pointer items-center justify-center"
          aria-label="Закрыть авторизацию"
        >
          <SvgoClose aria-hidden="true" filled class="!mb-0 text-2xl" />
        </DialogClose>
        <div>
          <DialogDescription
            class="text-gray text-2xl leading-7.5 mb-3 uppercase"
            >Личный кабинет</DialogDescription
          >
          <DialogTitle class="text-2xl leading-7.5 font-medium uppercase">
            {{ title }}
          </DialogTitle>
        </div>

        <div class="mt-16 mb-3 text-7xl" v-if="mode === 'reset-success'">
            <SvgoDone  class="text-blue mx-auto"/>
        </div>
        <p
          class="my-8 text-base leading-5"
          :class="{
            'text-center text-balance text-xl': mode === 'reset-success',
          }"
        >
          {{ description }}
        </p>

        <p
          v-if="authStore.errorMessage"
          role="alert"
          class="mt-5 rounded-lg bg-red/10 px-3 py-2 text-sm leading-5 text-red"
        >
          {{ authStore.errorMessage }}
        </p>

        <UiSmartCaptcha ref="smartCaptcha" />

        <form v-if="mode === 'login'" class="" @keydown.enter="handleFormEnter" @submit="submitLogin">
          <div class="mb-5">
            <UiInput
              name="email"
              type="email"
              autocomplete="email"
              placeholder="E-mail"
              variant="outline"
              class="w-full !border-black !text-black"
              :form="loginForm"
            />
            <p
              v-if="loginForm.errors.value.email"
              class="mt-1 text-xs text-red"
            >
              {{ loginForm.errors.value.email }}
            </p>
          </div>
          <div class="mb-3">
            <UiInput
              name="password"
              type="password"
              autocomplete="current-password"
              placeholder="Пароль"
              variant="outline"
              class="w-full !border-black !text-black"
              :form="loginForm"
            />
            <p
              v-if="loginForm.errors.value.password"
              class="mt-1 text-xs text-red"
            >
              {{ loginForm.errors.value.password }}
            </p>
          </div>
          <button
            type="button"
            class="border-b mb-6 text-base leading-5"
            @click="startPasswordReset"
          >
            Восстановить пароль
          </button>
          <div class="mb-6">
            <UiCheckbox name="consent" :form="loginForm">
              <span class="text-sm"
                >Согласен на
                <NuxtLink no-prefetch to="/pages/privacy-policy" class="underline"
                  >обработку персональных данных</NuxtLink
                >
                и получение уведомлений</span
              >
            </UiCheckbox>
            <p
              v-if="loginForm.errors.value.consent"
              class="mt-1 text-xs text-red"
            >
              {{ loginForm.errors.value.consent }}
            </p>
          </div>
          <UiButton
            type="submit"
            class="w-full mb-4"
            :disabled="authStore.isSubmitting"
            >Войти</UiButton
          >
          <UiButton
            type="button"
            variant="outline"
            class="w-full"
            :disabled="authStore.isSubmitting"
            @click="setMode('register')"
            >Зарегистрироваться</UiButton
          >
        </form>

        <form
          v-else-if="mode === 'register'"
          class="mt-6 space-y-4"
          @keydown.enter="handleFormEnter" @submit="submitRegistration"
        >
          <div>
            <UiInput
              name="firstName"
              autocomplete="given-name"
              placeholder="Имя"
              variant="outline"
              class="w-full !border-black !text-black"
              :form="registrationForm"
            />
            <p
              v-if="registrationForm.errors.value.firstName"
              class="mt-1 text-xs text-red"
            >
              {{ registrationForm.errors.value.firstName }}
            </p>
          </div>
          <div>
            <UiInput
              name="email"
              type="email"
              autocomplete="email"
              placeholder="E-mail"
              variant="outline"
              class="w-full !border-black !text-black"
              :form="registrationForm"
            />
            <p
              v-if="registrationForm.errors.value.email"
              class="mt-1 text-xs text-red"
            >
              {{ registrationForm.errors.value.email }}
            </p>
          </div>
          <div>
            <UiInput
              name="password"
              type="password"
              autocomplete="new-password"
              placeholder="Пароль"
              variant="outline"
              class="w-full !border-black !text-black"
              :form="registrationForm"
            />
            <p
              v-if="registrationForm.errors.value.password"
              class="mt-1 text-xs text-red"
            >
              {{ registrationForm.errors.value.password }}
            </p>
          </div>
          <div>
            <UiCheckbox name="consent" :form="registrationForm">
              <span class="text-sm"
                >Даю согласие на
                <NuxtLink no-prefetch to="/pages/privacy-policy" class="underline"
                  >обработку персональных данных</NuxtLink
                ></span
              >
            </UiCheckbox>
            <p
              v-if="registrationForm.errors.value.consent"
              class="mt-1 text-xs text-red"
            >
              {{ registrationForm.errors.value.consent }}
            </p>
          </div>
          <UiButton
            type="submit"
            class="w-full uppercase"
            :disabled="authStore.isSubmitting"
            >Зарегистрироваться</UiButton
          >
          <UiButton
            type="button"
            variant="outline"
            class="w-full uppercase"
            :disabled="authStore.isSubmitting"
            @click="setMode('login')"
            >У меня уже есть аккаунт</UiButton
          >
        </form>

        <form
          v-else-if="mode === 'reset-email'"
          class="mt-6 space-y-4"
          @keydown.enter="handleFormEnter" @submit="submitResetEmail"
        >
          <div>
            <UiInput
              name="email"
              type="email"
              autocomplete="email"
              placeholder="E-mail"
              variant="outline"
              class="w-full !border-black !text-black"
              :form="resetEmailForm"
            />
            <p
              v-if="resetEmailForm.errors.value.email"
              class="mt-1 text-xs text-red"
            >
              {{ resetEmailForm.errors.value.email }}
            </p>
          </div>
          <UiButton
            type="submit"
            class="w-full uppercase"
            :disabled="authStore.isSubmitting"
            >Далее</UiButton
          >
        </form>

        <form
          v-else-if="mode === 'reset-otp'"
          class="mt-6 space-y-4"
          @keydown.enter="handleFormEnter" @submit="submitResetOtp"
        >
          <div>
            <PinInputRoot
              :model-value="resetOtpDigits"
              type="number"
              otp
              name="code"
              class="flex w-full justify-between gap-2"
              :disabled="authStore.isSubmitting"
              @update:model-value="updateResetOtp"
              @complete="completeResetOtp"
            >
              <PinInputInput
                v-for="index in 6"
                :key="index"
                :index="index - 1"
                class="flex size-11 rounded-lg border border-black bg-white text-center text-xl font-medium text-black outline-none transition-colors focus:border-black focus:ring-2 focus:ring-black/15 disabled:cursor-not-allowed disabled:opacity-50 lg:size-12"
              />
            </PinInputRoot>
            <p v-if="shouldShowResetOtpError" class="mt-1 text-xs text-red">
              {{ resetOtpForm.errors.value.code }}
            </p>
          </div>
          <UiButton
            type="submit"
            class="w-full uppercase"
            :disabled="authStore.isSubmitting"
            >Далее</UiButton
          >
          <UiButton
            type="button"
            variant="outline"
            class="w-full uppercase"
            :disabled="authStore.isSubmitting || !resetEmail"
            @click="resendResetCode"
            >Отправить код ещё раз</UiButton
          >
        </form>

        <form
          v-else-if="mode === 'reset-password'"
          class="mt-6 space-y-4"
          @keydown.enter="handleFormEnter" @submit="submitResetPassword"
        >
          <div>
            <UiInput
              name="password"
              type="password"
              autocomplete="new-password"
              placeholder="Новый пароль"
              variant="outline"
              class="w-full !border-black !text-black"
              :form="resetPasswordForm"
            />
            <p
              v-if="resetPasswordForm.errors.value.password"
              class="mt-1 text-xs text-red"
            >
              {{ resetPasswordForm.errors.value.password }}
            </p>
          </div>
          <div>
            <UiInput
              name="passwordConfirmation"
              type="password"
              autocomplete="new-password"
              placeholder="Повторите пароль"
              variant="outline"
              class="w-full !border-black !text-black"
              :form="resetPasswordForm"
            />
            <p
              v-if="resetPasswordForm.errors.value.passwordConfirmation"
              class="mt-1 text-xs text-red"
            >
              {{ resetPasswordForm.errors.value.passwordConfirmation }}
            </p>
          </div>
          <UiButton
            type="submit"
            class="w-full uppercase"
            :disabled="authStore.isSubmitting"
            >Далее</UiButton
          >
        </form>

        <div v-else-if="mode === 'reset-success'" class="mt-6">
          <UiButton type="button" class="w-full uppercase" @click="showLogin"
            >Войти</UiButton
          >
        </div>

        <div
          v-if="!isPasswordResetMode"
          class="my-6 flex items-center gap-3 text-xs uppercase text-black/40"
        >
          <span class="h-px flex-1 bg-black/10" />или<span
            class="h-px flex-1 bg-black/10"
          />
        </div>
        <button
          v-if="!isPasswordResetMode"
          type="button"
          class="flex h-12 w-full items-center justify-center gap-2 rounded-lg border border-black text-sm font-medium transition-colors hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="authStore.isSubmitting"
          @click="authStore.startYandexLogin"
        >
          Войти с Яндекс ID
        </button>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style scoped>
.auth-drawer-content[data-state="open"] {
  animation: bottomSheetIn 220ms ease-out;
}

.auth-drawer-content[data-state="closed"] {
  animation: bottomSheetOut 180ms ease-in;
}

@media (min-width: 64rem) {
  .auth-drawer-content[data-state="open"] {
    animation: sideDrawerIn 220ms ease-out;
  }

  .auth-drawer-content[data-state="closed"] {
    animation: sideDrawerOut 180ms ease-in;
  }
}
</style>
